import React, { useState } from 'react'
import { Button, Flex, Form, Input, InputNumber } from 'antd'
import { actions } from '../../../../store/reducers/assignment.reducer'
import { connect } from 'react-redux'
import { useParams } from 'react-router'
import Text from 'antd/es/typography/Text'
import Access, {
    useAssignmentAccess,
} from '../access/Access'
import { AssignmentPermissions, Forbiddances } from '../../../../constants/assignment-permissions'
import { isNil } from 'lodash'
import ModalWithEditor from '../file-drawer/modal-with-editor/ModalWithEditor'
import TextEditor from '../../../../components/common/text-editor/TextEditor'
import QuillRenderer from '../../../../components/common/text-editor/QuillRenderer'
import ConceptualLabelSelect from '../../../resources/conceptual-labels/select/ConceptualLabelSelect'
import { useAutoFocusFirstInput } from '../../../../../hooks/useAutoFocusFirstInput'

const EditClusterFooter = ({ isWaitingForClusterSave, id }) => {
    return (
        <>
            <Button type='primary' htmlType='submit' form={`editCluster-${id}`} loading={isWaitingForClusterSave}>
                Save
            </Button>
        </>
    )
}

const EditClusterModal = ({
    id,
    name,
    feedback,
    grade,
    showEditClusterModal,
    setShowEditClusterModal,
    saveClusterFeedback,
    conceptualLabels,
}) => {
    const { token, layerId } = useParams()
    const [form] = Form.useForm()
    useAutoFocusFirstInput()
    const [isWaitingForClusterSave, setIsWaitingForClusterSave] = useState(false)

    const hasEditingPermissions = useAssignmentAccess([AssignmentPermissions.CAN_EDIT])

    const handleEditClusterModalClose = () => setShowEditClusterModal(false)

    const handleSaveCluster = async values => {
        setIsWaitingForClusterSave(true)
        await saveClusterFeedback(token, layerId, id, values.name, values.feedback, values.grade, values.conceptualLabels)
        setIsWaitingForClusterSave(false)
        setShowEditClusterModal(false)
    }

    const hasEditingFeedbackPermission = useAssignmentAccess(
        [AssignmentPermissions.CAN_EDIT],
        [
            Forbiddances.IS_VERSION_PUBLISHED,
            Forbiddances.IS_VERSION_NOT_LATEST,
            Forbiddances.VERSION_HAS_MBAS,
            Forbiddances.IS_PROCESSING,
        ],
    )

    return (
        <ModalWithEditor
            title={hasEditingFeedbackPermission ? 'Edit group' : 'View group'}
            open={showEditClusterModal}
            footer={
                hasEditingFeedbackPermission ? (
                    <EditClusterFooter isWaitingForClusterSave={isWaitingForClusterSave} id={id} />
                ) : null
            }
            forceRender
            onCancel={handleEditClusterModalClose}
            width={1200}
        >
            <Form
                name={`editCluster-${id}`}
                onFinish={handleSaveCluster}
                form={form}
                autoComplete='off'
                layout='vertical'
                requiredMark={false}
            >
                <Form.Item
                    name='name'
                    rules={[{ required: true, message: 'Please, enter name for this group' }]}
                    label='Name'
                    initialValue={name}
                >
                    <Access
                        permissions={[AssignmentPermissions.CAN_EDIT]}
                        fallback={<Text>{name || 'This group has no name'}</Text>}
                    >
                        <Input />
                    </Access>
                </Form.Item>
                <Form.Item name='feedback' initialValue={feedback}>
                    <Access
                        permissions={[AssignmentPermissions.CAN_EDIT]}
                        skipForbiddances={[
                            Forbiddances.IS_VERSION_PUBLISHED,
                            Forbiddances.IS_VERSION_NOT_LATEST,
                            Forbiddances.VERSION_HAS_MBAS,
                            Forbiddances.IS_PROCESSING,
                        ]}
                        fallback={<QuillRenderer htmlContent={feedback || 'This group has no feedback'} />}
                    >
                        <TextEditor label='Feedback' />
                    </Access>
                </Form.Item>
                <Flex justify='space-between' gap={16}>
                    <Form.Item
                        name='grade'
                        label='Grade'
                        initialValue={grade ?? 0}
                        rules={[{ type: 'number', min: 0 }]}
                    >
                        <Access
                            permissions={[AssignmentPermissions.CAN_EDIT]}
                            fallback={<Text>{isNil(grade) ? 'This group has no grade' : `${grade}%`}</Text>}
                        >
                            <InputNumber addonAfter='%' style={{ width: 120 }} />
                        </Access>
                    </Form.Item>
                    <Form.Item
                        name='conceptualLabels'
                        label='Conceptual labels'
                        initialValue={conceptualLabels}
                        tooltip='Conceptual labels identify the key  principles to be demonstrated in student work. Select labels that connect specific work patterns to broader educational concepts the student demonstrates or needs to develop.'
                    >
                        <ConceptualLabelSelect disabled={!hasEditingPermissions} />
                    </Form.Item>
                </Flex>
            </Form>
        </ModalWithEditor>
    )
}

const mapDispatch = dispatch => {
    return {
        saveClusterFeedback: (token, layerId, id, name, feedback, grade, conceptualLabels) => {
            return dispatch(actions.saveClusterFeedback(token, layerId, id, name, feedback, grade, conceptualLabels))
        },
    }
}

export default connect(null, mapDispatch)(EditClusterModal)
